import { format, parseISO } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { calendarDayVariants, controlTriggerVariants } from '@/components/ui/button-variants';
import { IconButton } from '@/components/ui/IconButton';
import { cn } from '@/lib/cn';

type Props = {
  value: string;
  onChange: (yearMonth: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
};

function parseYearMonth(value: string): { year: number; month: number } {
  const [y, m] = value.split('-').map(Number);
  return { year: y ?? new Date().getFullYear(), month: m ?? new Date().getMonth() + 1 };
}

export function MonthPicker({
  value,
  onChange,
  disabled,
  className,
  placeholder,
  'aria-label': ariaLabel
}: Props) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const locale = i18n.language.startsWith('ar') ? ar : enUS;

  const { year: selectedYear } = parseYearMonth(
    value || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  );
  const [viewYear, setViewYear] = useState(selectedYear);

  useEffect(() => {
    if (open) setViewYear(selectedYear);
  }, [open, selectedYear]);

  const display =
    value && value.length >= 7
      ? format(parseISO(`${value}-01`), 'MMMM yyyy', { locale })
      : (placeholder ?? t('ui.monthPicker.placeholder'));

  useEffect(() => {
    if (!open || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    setPanelStyle({ top: rect.bottom + 4, left: rect.left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const monthLabels = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(viewYear, i, 1);
      return { index: i + 1, label: format(d, 'MMM', { locale }) };
    });
  }, [viewYear, locale]);

  const rtl = i18n.dir() === 'rtl';
  const PrevIcon = rtl ? ChevronRight : ChevronLeft;
  const NextIcon = rtl ? ChevronLeft : ChevronRight;

  const panel =
    open && panelStyle
      ? createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={ariaLabel ?? t('ui.monthPicker.label')}
            className="fixed z-[200] rounded-[var(--fv-radius-lg)] border border-[var(--fv-border)] bg-[var(--fv-surface-elevated)] shadow-[var(--fv-shadow-md)]"
            style={{ top: panelStyle.top, left: panelStyle.left }}
          >
            <div className="w-[280px] select-none p-2">
              <div className="mb-3 flex items-center justify-between gap-2">
                <IconButton
                  size="sm"
                  aria-label={t('ui.calendar.prevMonth')}
                  onClick={() => setViewYear((y) => y - 1)}
                >
                  <PrevIcon className="h-4 w-4" />
                </IconButton>
                <span className="text-sm font-semibold">{viewYear}</span>
                <IconButton
                  size="sm"
                  aria-label={t('ui.calendar.nextMonth')}
                  onClick={() => setViewYear((y) => y + 1)}
                >
                  <NextIcon className="h-4 w-4" />
                </IconButton>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {monthLabels.map(({ index, label }) => {
                  const ym = `${viewYear}-${String(index).padStart(2, '0')}`;
                  const sel = value === ym;
                  return (
                    <button
                      key={ym}
                      type="button"
                      onClick={() => {
                        onChange(ym);
                        setOpen(false);
                      }}
                      className={cn(
                        calendarDayVariants({ selected: sel, outside: false }),
                        'h-9 text-xs'
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className={cn('relative inline-block w-full min-w-[10rem]', className)}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel ?? t('ui.monthPicker.label')}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        className={cn(controlTriggerVariants({ open }), !value && 'text-[var(--fv-text-muted)]')}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className="truncate">{display}</span>
        <CalendarIcon className="h-4 w-4 shrink-0 text-[var(--fv-text-muted)]" aria-hidden />
      </button>
      {panel}
    </div>
  );
}
